/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/forbid-prop-types */
import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import {
    Box, Typography, Avatar, TextField, Chip, Stack, Button, Collapse, Tooltip,
    InputAdornment, CircularProgress, Autocomplete, alpha, MenuItem,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import { callApi } from 'src/utils/apiCall';
import { common } from 'src/theme/palette';

const SEARCH_FIELDS = 'id,first_name,last_name,lead_contact';

/**
 * MultiPatientPicker — used for PATIENT_LIST audience mode.
 *
 *   Top: saved-segment dropdown ("Use saved segment")
 *   Bottom: multi-select patient autocomplete with chips for selected leads.
 *
 * Selecting a saved segment clears any individually picked patients (and
 * vice versa) so the wizard always submits exactly one of `lead_ids` or
 * `audience_id`, matching the API contract.
 */
const MultiPatientPicker = ({
    practiceId,
    selectedPatients,
    onPatientsChange,
    selectedSegment,
    onSegmentChange,
}) => {
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [segments, setSegments] = useState([]);
    const [segmentsLoading, setSegmentsLoading] = useState(false);

    // ── Load saved audiences (segments) ─────────────────────────────────────
    useEffect(() => {
        if (!practiceId) return;
        setSegmentsLoading(true);
        callApi(
            `/api/v1/practices/${practiceId}/audiences/?ordering=-created_at&limit=100&fields={recepient_count,audience_internal_queryset,description,id,name,type}`
        )
            .then((res) => {
                if (res.status === 200 && res.data?.results) {
                    setSegments(res.data.results);
                }
            })
            .catch(() => {})
            .finally(() => setSegmentsLoading(false));
    }, [practiceId]);

    const doSearch = useCallback(
        debounce(async (query) => {
            if (!query || query.length < 2 || !practiceId) {
                setSearchResults([]);
                setSearchLoading(false);
                return;
            }
            setSearchLoading(true);
            try {
                const res = await callApi(
                    `/api/v1/practices/${practiceId}/leads/search/?query=${encodeURIComponent(query)}&limit=10&offset=0&ordering=-created_at&is_archived=false&fields={${SEARCH_FIELDS}}`
                );
                if ((res.ok || res.status === 200) && res.data?.results) {
                    setSearchResults(res.data.results);
                } else {
                    setSearchResults([]);
                }
            } catch {
                setSearchResults([]);
            }
            setSearchLoading(false);
        }, 300),
        [practiceId]
    );

    const handleSegmentChange = (e) => {
        const id = e.target.value;
        if (!id) {
            onSegmentChange(null);
            return;
        }
        const seg = segments.find((s) => String(s.id) === String(id));
        if (seg) {
            onSegmentChange(seg);
            // Mode-exclusivity: choosing a segment clears individual picks
            onPatientsChange([]);
        }
    };

    const getName = (lead) =>
        `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'Unnamed';

    const getInitials = (lead) =>
        `${(lead.first_name || '?')[0]}${(lead.last_name || '?')[0]}`.toUpperCase();

    const handleAdd = (_, newVal) => {
        if (!newVal) return;
        if (selectedPatients.find((p) => p.id === newVal.id)) return;
        onPatientsChange([...selectedPatients, newVal]);
        // Clear input + results so the dropdown closes cleanly
        setInputValue('');
        setSearchResults([]);
        // Mode-exclusivity: choosing individual leads clears the segment
        if (selectedSegment) onSegmentChange(null);
    };

    const handleRemove = (id) => {
        onPatientsChange(selectedPatients.filter((p) => p.id !== id));
    };

    // ── Live audience count + preview ──────────────────────────────────────
    // Source of truth differs by mode:
    //   - Saved segment → AudienceSerializer.recepient_count (already on the
    //     segment object; mirrors `audience.get_recepient_count()`).
    //   - Manual list   → just the chip-array length.
    const [showPreview, setShowPreview] = useState(false);
    const audienceCount = useMemo(() => {
        if (selectedSegment) {
            return (
                selectedSegment.recepient_count
                ?? selectedSegment.audience_internal_queryset?.lead_count
                ?? selectedSegment.lead_count
                ?? 0
            );
        }
        return selectedPatients.length;
    }, [selectedSegment, selectedPatients]);

    // Preview rows: for manual mode use the chips (first 20 by id); for
    // segments we don't have a cheap members endpoint, so fall back to a
    // tooltip on the count chip listing the segment description.
    const previewRows = useMemo(() => {
        if (selectedSegment) return [];
        return selectedPatients.slice(0, 20);
    }, [selectedSegment, selectedPatients]);

    const hasAudience = audienceCount > 0;

    return (
        <Stack spacing={1.5}>
            {/* Saved segment dropdown */}
            <Box>
                <Typography
                    variant="caption"
                    sx={{
                        display: 'block', mb: 0.5, fontSize: '11px', fontWeight: 600,
                        color: 'text.secondary',
                    }}
                >
                    Use a saved segment (optional)
                </Typography>
                <TextField
                    select
                    size="small"
                    fullWidth
                    value={selectedSegment?.id || ''}
                    onChange={handleSegmentChange}
                    placeholder="Pick a saved segment"
                    disabled={segmentsLoading}
                    sx={{
                        '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '13px', bgcolor: '#fff' },
                        '& .MuiSelect-select': { py: 0.85 },
                    }}
                    inputProps={{ 'aria-label': 'Saved segment' }}
                >
                    <MenuItem value="" sx={{ fontSize: '13px', color: 'text.secondary' }}>
                        <em>None — pick patients individually</em>
                    </MenuItem>
                    {segments.map((s) => {
                        const count = s.recepient_count
                            ?? s.audience_internal_queryset?.lead_count
                            ?? s.lead_count
                            ?? 0;
                        return (
                            <MenuItem key={s.id} value={s.id} sx={{ fontSize: '13px' }}>
                                {s.name}
                                <Typography
                                    component="span"
                                    sx={{ ml: 1, fontSize: '11px', color: 'text.secondary' }}
                                >
                                    ({count})
                                </Typography>
                            </MenuItem>
                        );
                    })}
                </TextField>
            </Box>

            {/* Manual multi-select */}
            <Box>
                <Typography
                    variant="caption"
                    sx={{
                        display: 'block', mb: 0.5, fontSize: '11px', fontWeight: 600,
                        color: 'text.secondary',
                    }}
                >
                    {selectedSegment ? 'Or override with specific patients' : 'Add patients individually'}
                </Typography>
                <Autocomplete
                    fullWidth
                    filterOptions={(x) => x}
                    options={searchResults}
                    loading={searchLoading}
                    value={null}
                    inputValue={inputValue}
                    onChange={handleAdd}
                    onInputChange={(_, val, reason) => {
                        setInputValue(val);
                        if (reason === 'input') doSearch(val);
                    }}
                    getOptionLabel={(opt) => (opt ? getName(opt) : '')}
                    isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                    disabled={Boolean(selectedSegment)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            size="small"
                            placeholder={
                                selectedSegment
                                    ? 'Clear the segment to pick patients manually'
                                    : 'Search by name, email or phone…'
                            }
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Iconify icon="eva:search-fill" width={18} sx={{ color: 'text.disabled' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <>
                                        {searchLoading && <CircularProgress size={14} />}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '13px', bgcolor: '#fff' },
                            }}
                        />
                    )}
                    renderOption={(props, lead) => (
                        <Box
                            component="li"
                            {...props}
                            key={lead.id}
                            sx={{
                                display: 'flex', alignItems: 'center', gap: 1.5, py: 1, px: 1.5,
                                '&:hover': { bgcolor: alpha(common.mintyZest, 0.04) },
                            }}
                        >
                            <Avatar
                                sx={{
                                    width: 28, height: 28, fontSize: '11px', fontWeight: 700,
                                    bgcolor: alpha('#078DEE', 0.12), color: '#078DEE',
                                }}
                            >
                                {getInitials(lead)}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 600 }} noWrap>
                                    {getName(lead)}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '11px', color: 'text.secondary' }} noWrap>
                                    {lead.lead_contact?.email || lead.lead_contact?.phone || 'No contact info'}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    noOptionsText={inputValue.length < 2 ? 'Type at least 2 characters to search' : 'No patients found'}
                />

                {/* Selected chips */}
                {selectedPatients.length > 0 && (
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selectedPatients.map((lead) => (
                            <Chip
                                key={lead.id}
                                label={getName(lead)}
                                size="small"
                                onDelete={() => handleRemove(lead.id)}
                                deleteIcon={<Iconify icon="eva:close-fill" width={12} />}
                                sx={{
                                    height: 24, fontSize: '11.5px', fontWeight: 600,
                                    bgcolor: alpha(common.mintyZest, 0.08),
                                    color: common.mintyZest,
                                    borderRadius: '6px',
                                    '& .MuiChip-deleteIcon': { color: common.mintyZest, mr: 0.25 },
                                }}
                            />
                        ))}
                    </Box>
                )}

                {selectedPatients.length === 0 && !selectedSegment && (
                    <Typography
                        variant="caption"
                        sx={{ display: 'block', mt: 0.5, fontSize: '10.5px', color: '#919EAB' }}
                    >
                        Pick at least one patient or choose a saved segment.
                    </Typography>
                )}
            </Box>

            {/* ── Audience size + preview ─────────────────────────────────── */}
            {hasAudience && (
                <Box
                    sx={{
                        p: 1, borderRadius: '10px',
                        bgcolor: alpha(common.mintyZest, 0.04),
                        border: '1px solid',
                        borderColor: alpha(common.mintyZest, 0.18),
                    }}
                >
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Tooltip
                            title={
                                selectedSegment
                                    ? selectedSegment.description || 'Saved segment'
                                    : 'Patients selected manually'
                            }
                            disableInteractive
                        >
                            <Chip
                                size="small"
                                icon={<Iconify icon="solar:users-group-rounded-bold-duotone" width={12} />}
                                label={`${audienceCount} contact${audienceCount === 1 ? '' : 's'} match this ${selectedSegment ? 'segment' : 'list'}`}
                                sx={{
                                    height: 22, fontSize: '11px', fontWeight: 700,
                                    bgcolor: alpha(common.mintyZest, 0.1),
                                    color: common.mintyZest,
                                    borderRadius: '6px',
                                    '& .MuiChip-icon': { color: common.mintyZest, ml: 0.5 },
                                }}
                            />
                        </Tooltip>
                        <Box sx={{ flex: 1 }} />
                        {!selectedSegment && previewRows.length > 0 && (
                            <Button
                                size="small"
                                onClick={() => setShowPreview((v) => !v)}
                                startIcon={
                                    <Iconify
                                        icon={showPreview ? 'eva:eye-off-fill' : 'eva:eye-fill'}
                                        width={12}
                                    />
                                }
                                sx={{
                                    fontSize: '10.5px', fontWeight: 600, textTransform: 'none',
                                    color: common.mintyZest, py: 0.25, minHeight: 0, px: 0.75,
                                    '&:hover': { bgcolor: alpha(common.mintyZest, 0.08) },
                                }}
                            >
                                {showPreview ? 'Hide preview' : 'Preview list'}
                            </Button>
                        )}
                    </Stack>
                    <Collapse in={showPreview && previewRows.length > 0}>
                        <Stack spacing={0.25} sx={{ mt: 0.75 }}>
                            {previewRows.map((lead) => (
                                <Box
                                    key={lead.id}
                                    sx={{
                                        display: 'flex', alignItems: 'center', gap: 0.75,
                                        py: 0.25, px: 0.5, borderRadius: '6px',
                                    }}
                                >
                                    <Avatar
                                        sx={{
                                            width: 20, height: 20, fontSize: '9px', fontWeight: 700,
                                            bgcolor: alpha('#078DEE', 0.12), color: '#078DEE',
                                        }}
                                    >
                                        {getInitials(lead)}
                                    </Avatar>
                                    <Typography variant="body2" sx={{ fontSize: '11.5px', fontWeight: 600 }} noWrap>
                                        {getName(lead)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: '10.5px', color: 'text.secondary' }} noWrap>
                                        {lead.lead_contact?.email || lead.lead_contact?.phone || ''}
                                    </Typography>
                                </Box>
                            ))}
                            {selectedPatients.length > previewRows.length && (
                                <Typography variant="caption" sx={{ fontSize: '10.5px', color: '#919EAB', pl: 0.5 }}>
                                    +{selectedPatients.length - previewRows.length} more
                                </Typography>
                            )}
                        </Stack>
                    </Collapse>
                </Box>
            )}
        </Stack>
    );
};

MultiPatientPicker.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    selectedPatients: PropTypes.array,
    onPatientsChange: PropTypes.func.isRequired,
    selectedSegment: PropTypes.object,
    onSegmentChange: PropTypes.func.isRequired,
};

MultiPatientPicker.defaultProps = {
    selectedPatients: [],
    selectedSegment: null,
};

export default MultiPatientPicker;
